import { useState, useRef, useMemo, useEffect } from "react"
import arrayUtilities from "../../utilities/arrayUtilities";
import stringHelpers from "../../utilities/stringHelpers";

export const TagSelector = ({ tags, selectedTags, onChange, width, isDisabled = false, isClearable = true }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const componentRef = useRef(null);
    const runFilter = () => {
        if (stringHelpers.isNullOrEmpty(searchQuery)) return tags;
        return tags.filter(option =>
            option.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }

    const filteredOptions = useMemo(() => runFilter(), [searchQuery, tags]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            const isScrollContainerClick = event.target.classList.contains("tag-selector-scroll-container")
            if (componentRef.current && !componentRef.current.contains(event.target) && !isScrollContainerClick) {
                setSearchQuery('');
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();
                setSearchQuery('');
                setIsOpen(false);
            }
        };

        document.addEventListener('keydown', handleEscapeKey, true);

        return () => {
            document.removeEventListener('keydown', handleEscapeKey, true);
        };
    }, [isOpen]);

    const handleToggle = (value) => {
        const current = selectedTags || [];
        const newValues = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];
        onChange(newValues, value);
    };

    const close = () => {
        setSearchQuery('');
        setIsOpen(false);
    };

    const selectedCount = selectedTags?.length ?? 0;

    return (
        <div className="tag-selector border rounded position-relative search-select search-select-multi bg-white" ref={ componentRef } style={ { width: `${ width !== undefined ? width : "100%" }` } }>
            <div
                onClick={ () => !isDisabled && setIsOpen(!isOpen) }
                className={ `d-flex align-items-center justify-content-between w-100 pl-2 pr-0 bg-white py-1 ${ isOpen ? "is-open" : "" }` }
            >
                <div className="d-flex flex-wrap">
                    {
                        selectedCount === 0
                            ? <button className="btn btn-none m-0 p-0 fs--1">Select tags</button>
                            : !arrayUtilities.isNullOrEmpty(tags) && <div className="selected-tags-pills">
                                {
                                    selectedTags
                                    .slice(0, 3)
                                    .map(item => tags.find(tag => tag.id === item))
                                    .map(
                                        tag =>
                                            tag!== undefined && 
                                            <div
                                                key={ tag.id }
                                                className="d-flex align-items-center badge badge-pill badge-light text-secondary py-2 px-2 border position-relative"
                                            >
                                                <i className="fas fa-circle mr-2 ml-1 mb-0 fs--1" style={ {
                                                    color: `#${ tag.colourCode }`
                                                } }></i>
                                                <p className="m-0 mr-1">{ tag.name }</p>
                                                <button onClick={ event => {
                                                    event.stopPropagation()
                                                    handleToggle(tag.id)
                                                } } disabled={ isDisabled } className="btn btn-none p-0 m-0" type="button">
                                                    <i className="fas fa-times-circle"></i>
                                                </button>
                                            </div>
                                    )
                                }
                                {
                                    selectedCount > 3 && <p className="m-0 fs--1">+{selectedTags.slice(3).length} more</p>
                                }
                            </div>
                    }
                </div>

                { isOpen ? <i className="fas fa-chevron-up fs--2"></i> : <i className="fas fa-chevron-down fs--2"></i> }
            </div>
            { isOpen && (
                <div className="px-0 border rounded bg-white position-absolute top-100 left-0 search-select-dropdown" style={ { zIndex: 10 } }>
                    <div className="px-1 w-100">
                        <input
                            autoFocus
                            placeholder="Search..."
                            className="form-control my-2 px-2 fs--1 w-100"
                            type="text"
                            value={ searchQuery }
                            onChange={ e => setSearchQuery(e.target.value) }
                        />
                    </div>
                    {
                        !arrayUtilities.isNullOrEmpty(filteredOptions)
                            ? <ul className="pl-0 w-100 mb-0">
                                { filteredOptions.map((option) => {
                                    const isChecked = (selectedTags || []).includes(option.id);
                                    return (
                                        <li
                                            key={ option.id }
                                            onClick={ () => handleToggle(option.id) }
                                            className={ `dropdown-item cursor-pointer my-1 ml-0 pl-2 fs--1 d-flex align-items-center ${ isChecked ? "active" : "" }` }
                                        >
                                            <input
                                                type="checkbox"
                                                readOnly
                                                checked={ isChecked }
                                                className="mr-2"
                                                style={ { pointerEvents: "none" } }
                                            />
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-circle mr-2 ml-1 mb-0" style={ {
                                                    color: `#${ option.colourCode }`
                                                } }></i>
                                                <span>{ option.name }</span>
                                            </div>
                                        </li>
                                    );
                                }) }
                            </ul>
                            : <p className="py-2 mb-2 fs--1 d-flex justify-content-center">No results found</p>
                    }
                    <div className="p-2 border-top mt-1 bg-white d-flex justify-content-end">
                        {
                            isClearable &&
                            <button onClick={ () => {
                                setSearchQuery("")
                                onChange([])
                            } } type="button" className="btn btn-falcon-default btn-sm mr-2">
                                Clear
                            </button>
                        }
                        <button onClick={ close } className="btn btn-primary btn-sm ml-1 nowrap">
                            Done
                        </button>
                    </div>
                </div>
            ) }
        </div>
    );
};
